import React, { useState, useContext, useEffect, useMemo, useRef } from 'react';

import Loader from './common/Loader';
import SearchBar from './common/SearchBar';

import axios_api from '../Api';
import { API_URL, AUTH_TOKEN } from '../Context';


export default function FormCopyContentModal({ targetForm, onSuccess }) {
  const api_url = useContext(API_URL);
  const token = useContext(AUTH_TOKEN);

  const dismissRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [forms, setForms] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const modalElement = document.getElementById('copyFormContentModal');
    if (!modalElement) return;

    const handleShow = () => {
      setSelectedFormId(null);
      setSearchText('');
      setYearFilter('');
      setError('');
      setLoading(true);
      axios_api.get(api_url + '/forms', {
        headers: { 'Authorization': token },
      }).then(
        (result) => {
          setForms(result.data || []);
          setLoading(false);
        },
        (err) => {
          console.log(err);
          setForms([]);
          setLoading(false);
        },
      );
    };

    modalElement.addEventListener('show.bs.modal', handleShow);
    return () => modalElement.removeEventListener('show.bs.modal', handleShow);
  }, [api_url, token]);

  const years = useMemo(() => {
    const unique = Array.from(new Set(forms.map((f) => f.year).filter(Boolean)));
    unique.sort((a, b) => b - a);
    return unique;
  }, [forms]);

  const filteredForms = useMemo(() => {
    const search = searchText.toLowerCase();
    return forms
      .filter((f) => f.id !== targetForm?.id)
      .filter((f) => (yearFilter ? String(f.year) === String(yearFilter) : true))
      .filter((f) => {
        if (!search) return true;
        const fields = [f.form_code, f.name, f.catalog_entry, f.section, String(f.year), String(f.cycle)];
        return fields.some((value) => (value || '').toLowerCase().includes(search));
      });
  }, [forms, searchText, yearFilter, targetForm]);

  const handleCopy = () => {
    if (!selectedFormId) {
      setError('Выберите форму для копирования');
      return;
    }
    setError('');
    setSubmitting(true);

    axios_api.post(
      api_url + `/copy_form_content/${targetForm.id}`,
      { source_form_id: selectedFormId },
      { headers: { 'Authorization': token } },
    ).then(
      () => {
        setSubmitting(false);
        dismissRef.current?.click();
        if (onSuccess) onSuccess();
      },
      (err) => {
        setSubmitting(false);
        const message = err.response?.data || 'Ошибка копирования содержимого формы';
        setError(typeof message === 'string' ? message : JSON.stringify(message));
        console.log(err);
      },
    );
  };

  return (
    <div
      className="modal fade"
      id="copyFormContentModal"
      tabIndex="-1"
      aria-labelledby="copyFormContentModalLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-dialog-scrollable modal-xl">
        <div className="modal-content">
          <div className="modal-header bg-light">
            <h2 className="modal-title fs-5" id="copyFormContentModalLabel">
              Скопировать содержимое формы
            </h2>
            <button
              type="button"
              className="btn-close me-3"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">
            {targetForm && (
              <p className="mb-3">
                Выберите форму, содержимое и настройки которой будут скопировано в текущую форму{' '}
                <strong>{targetForm.form_code} — {targetForm.name}</strong>.
              </p>
            )}

            <div className="d-flex align-items-center gap-3 mb-3">
              <SearchBar searchText={searchText} onSearchtextChange={setSearchText} />
              <select
                className="lk-select form-select"
                style={{ width: '160px' }}
                value={yearFilter}
                onChange={(e) => setYearFilter(e.target.value)}
              >
                <option value="">Все годы</option>
                {years.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            {loading ? (
              <Loader />
            ) : (
              <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}></th>
                      <th>код</th>
                      <th>имя формы</th>
                      <th style={{ width: '180px' }}>каталожный номер</th>
                      <th>год</th>
                      <th>цикл</th>
                      <th>section</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredForms.length === 0 && (
                      <tr>
                        <td colSpan="7" className="text-center text-muted">
                          Формы не найдены
                        </td>
                      </tr>
                    )}
                    {filteredForms.map((form) => (
                      <tr
                        key={form.id}
                        style={{ cursor: 'pointer' }}
                        className={selectedFormId === form.id ? 'table-active' : ''}
                        onClick={() => setSelectedFormId(form.id)}
                      >
                        <td>
                          <input
                            type="radio"
                            name="copyContentSourceForm"
                            checked={selectedFormId === form.id}
                            onChange={() => setSelectedFormId(form.id)}
                          />
                        </td>
                        <td>{form.form_code}</td>
                        <td>{form.name}</td>
                        <td>{form.catalog_entry}</td>
                        <td>{form.year}</td>
                        <td>{form.cycle}</td>
                        <td>{form.section}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {error && <p className="text-danger fw-bold mt-2 mb-0">{error}</p>}
          </div>
          <div className="modal-footer justify-content-between">
            <button
              type="button"
              className="btn btn-secondary lk-bootstrap-button"
              data-bs-dismiss="modal"
              disabled={submitting}
            >
              Отмена
            </button>
            <button
              type="button"
              className="btn lk-button lk-bootstrap-button"
              onClick={handleCopy}
              disabled={!selectedFormId || submitting}
            >
              {submitting ? 'Копирование...' : 'Скопировать содержимое'}
            </button>
            <button
              ref={dismissRef}
              type="button"
              data-bs-dismiss="modal"
              style={{ display: 'none' }}
              aria-hidden="true"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
